<template>
    <Dialog
        :width="750"
        persistent
        @close="$emit('close')"
    >
        <template v-slot:header-bubble>
            {{ bubbleText }}
        </template>
        <template v-slot:header-info>
            <div v-html="$t('If you want to use Uku for free then deactivate your account and continue with {0} plan').format(soloLink)"/> <!-- INPUT FROM STATIC TRANSLATION -->
        </template>
        <v-row no-gutters>
            <v-col
                v-for="(bundle, index) in show_bundles"
                class="pl-7"
                :key="bundle.id"
            >
                <div class="title">{{ getManifestData(bundle.name, "title") }}</div>
                <div class="title">{{ formatMoney(bundle.bundle_price.price_year, bundle.bundle_price.currency) }}</div>
                <div class="caption g4--text mb-4" style="line-height: 16px">
                    {{ $t("per member/month (billed yearly)") }}<br>
                    {{ $t("or {0} per member/month (monthly)").format(formatMoney(bundle.bundle_price.price_month, bundle.bundle_price.currency)) }}
                </div>
                <div>
                    <div v-if="index > 0" class="body-2 font-weight-bold mb-3">
                        {{ $t("Everything in {0}, plus:").format(getManifestData(show_bundles[0].name, "title")) }}
                    </div>
                    <v-row
                        v-for="(bullet, bulletIndex) in bullets[bundle.name]"
                        :key="bulletIndex"
                        no-gutters
                        class="d-flex flex-nowrap"
                    >
                        <v-col class="shrink pr-2 py-0">
                            <v-icon size="18" class="pa-0">check</v-icon>
                        </v-col>
                        <v-col :class="bullet.class" class="pa-0 shrink text-no-wrap">
                            {{ bullet.text }}
                        </v-col>
                        <v-col v-if="bullet.append" :class="[bullet.append_class]">
                            {{ bullet.append }}
                        </v-col>
                    </v-row>
                </div>
            </v-col>
        </v-row>
        <template v-slot:footer>
            <a class="body-2" target="_blank" href="/api/help_link/compare_bundles">{{ $t('Compare plans, feature-by-feature') }}</a>
            <v-spacer/>
            <danger-button @click="onDeactivateAccount">
                {{ $t("Deactivate account") }}
            </danger-button>
            <primary-button append-icon="mdi-arrow-right" @click="onChoosePlan">
                {{ $t("Choose plan") }}
            </primary-button>
        </template>
    </Dialog>
</template>

<script>
import {computed, ref} from "@vue/composition-api"
import Dialog from "../../components/Dialog/Dialog.vue"
import AppModule, {state as appState} from "@/store/modules/app"
import {formatMoney} from "@/plugins/formatting"
import {$t} from "@/i18n"
import DialogModule from "@/store/modules/dialog"
import SubscriptionModule from "@/store/modules/subscription"
import {getManifestData} from "@/views/AppStore/app-store-manifest"
import errorDialog from "@/views/ErrorDialog/errorDialog"
import {APP_STORE_APP_TYPES, APP_STORE_BUNDLE_TYPES} from "@/helpers/constants"

export default {
    name: "SubscriptionRequiredDialog",
    components: {
        Dialog
    },
    props: {
        account: {
            type: Object,
            required: true
        },
        featureNotEnabled: {
            type: Boolean,
            default: false
        }
    },
    setup(props, ctx) {
        const {fetchClassifiers, bundles, baseUrl} = AppModule()
        const {openConfirmDialog, setConfirmOptions} = DialogModule()
        const {deactivateCompany} = SubscriptionModule()

        const show_bundles = computed(() => {
            if (!bundles.value[props.account.id]) {
                return []
            }

            return bundles.value[props.account.id].filter(x => [APP_STORE_BUNDLE_TYPES.TEAM, APP_STORE_BUNDLE_TYPES.ENTERPRISE].includes(x.name))
        })

        const loadData = () => {
            fetchClassifiers({types: ["bundles"], account_id: props.account.id})
                .catch(ex => errorDialog.showError(ex))
        }
        const onChoosePlan = () => {
            ctx.root._router.push({
                name: "subscription_choose_plan",
                params: {id: props.account.id}
            })

            ctx.emit('close')
        }
        const onDeactivateAccount = () => {
            setConfirmOptions({
                title: $t("Are you sure to deactivate {0} account?").format(props.account.name),
                text: $t("If you choose to continue with freelancer plan your company account will be deactivated. Data that is related to this company will be removed."),
                confirm_text: $t("Deactivate"),
                cancel_text: $t("Cancel"),
                action: confirmDeactivateCompany
            })
            openConfirmDialog()
        }
        const confirmDeactivateCompany = () => {
            deactivateCompany(props.account.id)
                .then(() => {
                    window.location = baseUrl.value
                })
                .catch(ex => errorDialog.showError(ex))
        }

        const bullets = {
            [APP_STORE_BUNDLE_TYPES.TEAM]: [
                {text: $t('Teamwork')},
                {text: $t('Unlimited clients + import')},
                {text: $t('Access management')},
                {text: $t('Custom fields')},
                {text: $t('Live chat support')},
                {text: $t('Integrations'), class: 'font-weight-medium'},
                {text: $t('Billing'), class: 'font-weight-medium'},
                {text: $t('AML'), class: 'font-weight-medium'},
                {text: $t('API'), class: 'font-weight-medium'},
                {text: $t("Emails"), class: 'font-weight-medium'},
                {text: $t("Client Portal"), class: 'font-weight-medium', append: $t('New!'), append_class: 'pl-2 py-0 red--text shrink font-weight-medium'},
            ],
            [APP_STORE_BUNDLE_TYPES.ENTERPRISE]: [
                {text: $t('Custom integrations')},
                {text: $t('Assisted clients import')},
                {text: $t('Tailor-made team workshop')},
                {text: $t('Early access to features')},
                {text: $t('Pay by invoice')},
                {text: $t('Dedicated account manager')},
            ]
        }

        const bubbleText = computed(() => {
            if (!props.featureNotEnabled) {
                return $t('Hi! Your trial is over. Please choose {0} subscription plan to continue work').format(props.account.name)
            }

            return $t('Hi! You need to upgrade {0} subscription plan to use that feature').format(props.account.name)
        })

        const soloLink = computed(() => '<a target="_blank" href="/api/help_link/compare_bundles">Solo</a>')

        loadData()

        return {
            show_bundles,
            onChoosePlan,
            formatMoney,
            onDeactivateAccount,
            getManifestData,
            bullets,
            APP_STORE_BUNDLE_TYPES,
            bubbleText,
            soloLink,
        }
    }
}
</script>
